import { useState, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { extractErrorMessage, formatDateDDMMYYYY } from "@/shared/utils";
import { useAppDispatch, useLocale } from "@/shared/hooks";
import { setActiveProject } from "@/features/settings";
import { useLazyGetProjectAuditQuery } from "@/features/projects";
import { useGetCrossProjectBoardQuery } from "../api";
import { TASK_STATUS_MAP, PRIORITY_MAP } from "../consts";
import { generateTasksPdf, generateAuditPdf, formatAuditRowsForPdf } from "../utils";
import { useCrossProjectBoard } from "./useCrossProjectBoard";

type BoardTab = "overview" | "goals" | "list" | "kanban";

const VALID_TABS: BoardTab[] = ["overview", "goals", "list", "kanban"];

function isValidTab(value: string | null): value is BoardTab {
  return value !== null && VALID_TABS.includes(value as BoardTab);
}

export function useBoardPage() {
  const { t } = useTranslation("BoardPage");
  const { t: tTasks } = useTranslation("TasksPage");
  const locale = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const activeTab: BoardTab = isValidTab(tabParam) ? tabParam : "kanban";

  const projectParam = searchParams.get("project");
  const kpiParam = searchParams.get("kpi");
  const taskIdParam = searchParams.get("taskId");
  const projectId = projectParam ? Number(projectParam) : null;
  const kpiId = kpiParam ? Number(kpiParam) : null;
  const initialTaskId = taskIdParam ? Number(taskIdParam) : null;
  const isSingleProject = projectId !== null;

  const {
    data: crossProjectData,
    isLoading: isCrossProjectLoading,
    isFetching: isCrossProjectFetching,
  } = useGetCrossProjectBoardQuery();

  const crossProjectBoard = useCrossProjectBoard(crossProjectData);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setActiveProject(projectId));
  }, [projectId, dispatch]);

  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingAudit, setIsExportingAudit] = useState(false);
  const [triggerAuditFetch] = useLazyGetProjectAuditQuery();
  const [auditPrompt, setAuditPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(
    null,
  );

  async function handleExportPdf() {
    if (isExportingPdf) return;
    setIsExportingPdf(true);
    try {
      const allTasks = [
        ...crossProjectBoard.mergedColumns.flatMap((col) => col.tasks),
        ...crossProjectBoard.backlogTasks,
      ];
      const columns = [
        tTasks("tableView.task"),
        tTasks("tableView.status"),
        tTasks("tableView.priority"),
        tTasks("tableView.assignee"),
        tTasks("tableView.createdAt"),
        tTasks("tableView.dueDate"),
      ];
      const rows = allTasks.map((task) => ({
        cells: [
          task.title,
          tTasks(TASK_STATUS_MAP[task.status].label),
          tTasks(PRIORITY_MAP[task.priority].label),
          task.assigned_to_name || tTasks("tableView.unassigned"),
          formatDateDDMMYYYY(task.created_at),
          task.due_date ? formatDateDDMMYYYY(task.due_date) : tTasks("tableView.noDueDate"),
        ],
      }));
      await generateTasksPdf({
        projectName: t("header.title"),
        projectStatus: "",
        budget: "",
        dateRange: "",
        columns,
        rows,
        filename: `${t("header.title")}.pdf`,
        labels: {
          project: tTasks("export.project"),
          status: tTasks("export.status"),
          budget: tTasks("export.budget"),
          dates: tTasks("export.dates"),
          taskList: tTasks("export.taskList"),
        },
      });
    } finally {
      setIsExportingPdf(false);
    }
  }

  async function handleExportAuditPdf() {
    if (isExportingAudit) return;
    setIsExportingAudit(true);
    try {
      const result = await triggerAuditFetch({
        page_size: 1000,
        ...(projectId !== null && projectId > 0 ? { project: projectId } : {}),
      }).unwrap();
      const rows = formatAuditRowsForPdf(result.results, tTasks, locale);
      await generateAuditPdf({
        title: `${tTasks("export.project")}: ${t("header.title")}`,
        subtitle: tTasks("export.auditTitle"),
        columns: [
          tTasks("export.auditColumns.date"),
          tTasks("export.auditColumns.user"),
          tTasks("export.auditColumns.action"),
          tTasks("export.auditColumns.target"),
          tTasks("export.auditColumns.changes"),
        ],
        rows,
        filename: `${t("header.title")} — ${tTasks("export.auditTitle")}.pdf`,
        labels: {
          generatedAt: tTasks("export.auditColumns.date"),
        },
      });
    } catch (err: unknown) {
      setAuditPrompt({
        title: tTasks("messages.errorTitle"),
        text: extractErrorMessage(err, "messages.exportError", tTasks),
        variant: "error",
      });
    } finally {
      setIsExportingAudit(false);
    }
  }

  function handleTabChange(newTab: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", newTab);
      if (newTab === "overview") {
        next.delete("kpi");
      }
      return next;
    });
  }

  function handleNavigateToKpiBoard(kpiId: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "kanban");
      next.set("kpi", String(kpiId));
      return next;
    });
  }

  function handleKpiFilter(newKpiId: number | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newKpiId !== null) {
        next.set("kpi", String(newKpiId));
      } else {
        next.delete("kpi");
      }
      return next;
    });
  }

  const handleProjectFilter = useCallback(
    function handleProjectFilter(newProjectId: number | null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (newProjectId !== null) {
          next.set("project", String(newProjectId));
          next.delete("kpi");
        } else {
          next.delete("project");
          next.delete("kpi");
        }
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(function clearStaleKpi() {
    if (isCrossProjectLoading || isCrossProjectFetching) return;
    if (kpiId === null) return;
    const allKpiIds = crossProjectBoard.projectBoards.flatMap((b) => {
      const fromCols = b.columns.flatMap((c) => c.tasks);
      const fromBacklog = b.backlogColumn.tasks;
      return [...fromCols, ...fromBacklog].filter((t) => t.task_type === "kpi").map((t) => t.id);
    });
    if (!allKpiIds.includes(kpiId)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("kpi");
        return next;
      }, { replace: true });
    }
  }, [kpiId, isCrossProjectLoading, isCrossProjectFetching, crossProjectBoard.projectBoards, setSearchParams]);

  const clearTaskIdParam = useCallback(function clearTaskIdParam() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("taskId");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return {
    t,
    tTasks,
    activeTab,
    handleTabChange,
    projectId,
    kpiId,
    initialTaskId,
    isSingleProject,
    crossProjectData,
    crossProjectBoard,
    isCrossProjectLoading,
    isCrossProjectFetching,
    handleProjectFilter,
    handleNavigateToKpiBoard,
    handleKpiFilter,
    handleExportPdf,
    isExportingPdf,
    handleExportAuditPdf,
    isExportingAudit,
    auditPrompt,
    setAuditPrompt,
    clearTaskIdParam,
  };
}
